import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';

interface NodeGroupData {
  label: string;
  nodeIds: string[];
  collapsed: boolean;
}

const NodeGroup: React.FC<NodeProps<NodeGroupData>> = ({ 
  id, 
  data, 
  selected,
  isConnectable 
}) => {
  const [collapsed, setCollapsed] = useState(data.collapsed);
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);
  const [isDragOver, setIsDragOver] = useState(false);
  const { setNodes, getNode } = useReactFlow();
  
  const toggleCollapse = useCallback(() => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    
    // Update child nodes visibility
    setNodes(nodes => 
      nodes.map(node => {
        if (node.parentNode === id) {
          return {
            ...node,
            hidden: newCollapsed
          };
        }
        return node;
      })
    );
    
    // Update group data
    setNodes(nodes => 
      nodes.map(node => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              collapsed: newCollapsed
            }
          };
        }
        return node;
      })
    );
  }, [collapsed, id, setNodes]);
  
  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  }, []);
  
  const handleLabelBlur = useCallback(() => {
    setIsEditing(false);
    
    setNodes(nodes => 
      nodes.map(node => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              label
            }
          };
        }
        return node;
      })
    );
  }, [id, label, setNodes]);
  
  const handleLabelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLabelBlur();
    }
  }, [handleLabelBlur]);
  
  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    // Get the dragged node ID from the dataTransfer
    const nodeId = e.dataTransfer.getData('application/reactflow/nodeid');
    if (!nodeId || nodeId === id) return;
    
    const draggedNode = getNode(nodeId);
    if (!draggedNode) return;
    
    // Add node to group
    setNodes(nodes => 
      nodes.map(node => {
        if (node.id === nodeId) {
          // Update node to be part of the group
          return {
            ...node,
            parentNode: id,
            extent: 'parent',
            // Adjust position to be relative to the group
            position: {
              x: node.position.x - getNode(id)!.position.x,
              y: node.position.y - getNode(id)!.position.y,
            }
          };
        }
        
        if (node.id === id) {
          // Update group's nodeIds list
          return {
            ...node,
            data: {
              ...node.data,
              nodeIds: [...node.data.nodeIds, nodeId]
            }
          };
        }
        
        return node;
      })
    );
  }, [id, getNode, setNodes]);

  return (
    <div 
      className={`node-group rounded-lg border-2 ${
        selected ? 'border-blue-500' : isDragOver ? 'border-green-500' : 'border-gray-300'
      } bg-gray-100 bg-opacity-70 p-4`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
      
      <div className="flex items-center justify-between mb-2">
        {isEditing ? (
          <input
            type="text"
            value={label}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
            onKeyDown={handleKeyDown}
            className="bg-white border border-blue-400 px-1 font-medium focus:outline-none focus:border-blue-500"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div 
            className="px-1 font-medium cursor-text"
            onClick={handleLabelClick}
          >
            {label}
          </div>
        )}
        
        <button
          className="ml-2 p-1 rounded hover:bg-gray-200"
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapse();
          }}
        >
          {collapsed ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </div>
      
      {collapsed && (
        <div className="text-xs text-gray-500">
          {data.nodeIds.length} nodes (collapsed)
        </div>
      )}
      
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
    </div>
  );
};

export default memo(NodeGroup);
